import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import {
  clearMobileAccess,
  fetchMobileCaptureMe,
  logoutMobileAuth,
  mobilePasswordLogin,
  mobileSelectWorkspace,
  readMobileAccess,
  refreshMobileAuth,
  storeMobileAccess,
  type MobileCaptureTargetFlow,
  type MobileWorkspaceOption,
} from '../mobileCaptureApi'
import { MobileCaptureShell } from '../components/MobileCaptureShell'
import MobileAudioCapturePage from './MobileAudioCapturePage'
import MobileCapturePage from './MobileCapturePage'
import { isAudioCaptureFlow } from '../mobileCaptureApi'
import './MobileCapturePage.css'

type Gate = 'booting' | 'login' | 'select_workspace' | 'ready'

/**
 * /mobile-capture shell — password login, workspace pick, or restore session.
 * Capture UI is shared for QR-exchange and password login.
 */
export default function MobileCaptureApp() {
  const [gate, setGate] = useState<Gate>('booting')
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [workspaces, setWorkspaces] = useState<MobileWorkspaceOption[]>([])
  const [workspaceName, setWorkspaceName] = useState('')
  const [targetFlow, setTargetFlow] = useState<MobileCaptureTargetFlow>('item_image_processing')
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const accessTokenRef = useRef<string | null>(null)

  useEffect(() => {
    accessTokenRef.current = accessToken
  }, [accessToken])

  const enterReady = useCallback(async (token: string) => {
    const me = await fetchMobileCaptureMe(token)
    setAccessToken(token)
    storeMobileAccess(token)
    setWorkspaceName(me.workspace_name)
    setTargetFlow(me.target_flow || 'item_image_processing')
    setGate('ready')
    setError(null)
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const existing = readMobileAccess()
      if (existing) {
        try {
          await enterReady(existing)
          return
        } catch {
          clearMobileAccess()
        }
      }
      try {
        const refreshed = await refreshMobileAuth()
        if (cancelled) return
        await enterReady(refreshed.access_token)
      } catch {
        if (!cancelled) setGate('login')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [enterReady])

  // Quiet access refresh while capture is open (rolling session).
  useEffect(() => {
    if (gate !== 'ready') return
    const t = window.setInterval(() => {
      void refreshMobileAuth()
        .then((r) => {
          setAccessToken(r.access_token)
          accessTokenRef.current = r.access_token
        })
        .catch(() => undefined)
    }, 20 * 60 * 1000)
    return () => window.clearInterval(t)
  }, [gate])

  const onLogin = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await mobilePasswordLogin(email.trim(), password)
      if (result.needs_workspace_selection) {
        setWorkspaces(result.workspaces)
        setGate('select_workspace')
      } else if (result.access_token) {
        await enterReady(result.access_token)
      } else {
        setError('登录失败')
      }
    } catch (err) {
      setError((err as Error).message || '登录失败')
    } finally {
      setBusy(false)
    }
  }

  const onPickWorkspace = async (workspaceId: string) => {
    setBusy(true)
    setError(null)
    try {
      const issued = await mobileSelectWorkspace(email.trim(), password, workspaceId)
      await enterReady(issued.access_token)
    } catch (err) {
      setError((err as Error).message || '无法进入该工作区')
    } finally {
      setBusy(false)
    }
  }

  const getAccessToken = useCallback(() => accessTokenRef.current, [])

  const onAuthExpired = useCallback(() => {
    clearMobileAccess()
    setAccessToken(null)
    accessTokenRef.current = null
    setGate('login')
    setError('登录状态已失效，请重新登录。未上传的照片仍保留在下方列表中。')
  }, [])

  const onLogout = async () => {
    const tok = accessTokenRef.current
    if (tok) await logoutMobileAuth(tok)
    else clearMobileAccess()
    setAccessToken(null)
    accessTokenRef.current = null
    setPassword('')
    setGate('login')
  }

  if (gate === 'booting') {
    return (
      <MobileCaptureShell>
        <div className="mc-page">
          <header className="mc-header">
            <div className="mc-brand">VISTOERA</div>
            <h1>正在连接…</h1>
          </header>
        </div>
      </MobileCaptureShell>
    )
  }

  if (gate === 'login') {
    return (
      <MobileCaptureShell>
      <div className="mc-page">
        <header className="mc-header">
          <div className="mc-brand">VISTOERA</div>
          <h1>手机拍摄</h1>
          <p className="mc-subtitle">使用 Vistoera 账号登录，或扫描电脑端二维码</p>
        </header>
        {error && <div className="mc-banner mc-banner-error">{error}</div>}
        <form className="mc-login-form" onSubmit={(e) => void onLogin(e)}>
          <label className="mc-field">
            <span>邮箱</span>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label className="mc-field">
            <span>密码</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <button type="submit" className="mc-big-button mc-primary" disabled={busy}>
            {busy ? '登录中…' : '登录'}
          </button>
        </form>
        <p className="mc-subtitle" style={{ marginTop: 8 }}>
          也可以通过电脑端「图片资产 → 手机上传」扫码快速进入
        </p>
      </div>
      </MobileCaptureShell>
    )
  }

  if (gate === 'select_workspace') {
    return (
      <MobileCaptureShell>
      <div className="mc-page">
        <header className="mc-header">
          <div className="mc-brand">VISTOERA</div>
          <h1>选择工作区</h1>
          <p className="mc-subtitle">请选择要拍摄上传的工作区</p>
        </header>
        {error && <div className="mc-banner mc-banner-error">{error}</div>}
        <div className="mc-ws-list">
          {workspaces.map((ws) => (
            <button
              key={ws.workspace_id}
              type="button"
              className="mc-big-button mc-secondary"
              disabled={busy}
              onClick={() => void onPickWorkspace(ws.workspace_id)}
            >
              {ws.workspace_name}
            </button>
          ))}
        </div>
      </div>
      </MobileCaptureShell>
    )
  }

  const capturePage = isAudioCaptureFlow(targetFlow) ? (
    <MobileAudioCapturePage
      workspaceName={workspaceName}
      getAccessToken={getAccessToken}
      onAccessToken={(tok) => {
        setAccessToken(tok)
        accessTokenRef.current = tok
        storeMobileAccess(tok)
      }}
      onAuthExpired={onAuthExpired}
      onLogout={() => void onLogout()}
    />
  ) : (
    <MobileCapturePage
      workspaceName={workspaceName}
      targetFlow={targetFlow}
      getAccessToken={getAccessToken}
      onAccessToken={(tok) => {
        setAccessToken(tok)
        accessTokenRef.current = tok
        storeMobileAccess(tok)
      }}
      onAuthExpired={onAuthExpired}
      onLogout={() => void onLogout()}
    />
  )

  return <MobileCaptureShell>{capturePage}</MobileCaptureShell>
}
